<template>
  <el-select
    v-model="establishmentsModel"
    @change="establishmentsChanged"
    autocomplete="new-password"
    :placeholder="$t('COMMON.ESTABLISHMENTS')"
    :filterable="true"
    :multiple="true"
    :disabled="disabled"
    remote
    :remote-method="getEstablishments"
    :loading="loading"
  >
    <el-option
      v-for="establishment in establishmentsList"
      :key="establishment.id"
      :value="establishment.id"
      :label="optionLabel(establishment)"
    >
      <span>{{ establishment.company_name }}</span>
      <el-tag
        v-if="isInactive(establishment)"
        size="mini"
        type="danger"
        class="ml-2"
      >
        {{ $t("ESTABLISHMENTS.INACTIVE") }}
      </el-tag>
    </el-option>
  </el-select>
</template>

<script>
import { difference } from "lodash";
import { Option, Select, Tag } from "element-ui";

export default {
  name: "establishments-selector",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Tag.name]: Tag,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
      description: "Disable the input",
    },
    onlyActive: {
      type: Boolean,
      default: false,
      description:
        "Only list active establishments (creation flows). Already-selected establishments (preloaded by ids) stay visible so existing records still display.",
    },
    establishments: {
      type: Array,
      default: () => [],
      description: "Establishments",
    },
    organization: {
      type: String,
      default: null,
      description: "Organization id",
    },

    filterServiceCenters: {
      type: [Array, String],
      default: null,
      description: "Service centers id",
    },
  },

  data() {
    return {
      establishmentsModel: [...this.establishments].map((item) => item.id),
      establishmentsList: {},
    };
  },

  setup() {},

  created() {
    this.getEstablishments(null, this.establishments);
  },

  methods: {
    async getEstablishments(query = null, establishments = null) {
      try {
        this.loading = true;
        let params = {
          sort: "company_name",
          filter: {
            ...(query ? { search: encodeURIComponent(query) } : {}),
            ...(establishments ? { ids: establishments.map((item) => item.id) } : {}),
            // En création (onlyActive, ex. formulaire d'événement), n'affiche que
            // les établissements actifs. Le backend interdit de toute façon de
            // rattacher un établissement désactivé, donc on filtre sans exception.
            ...(this.onlyActive ? { active: 1 } : {}),
          },
          page: {
            number: 1,
            size: 10,
          },
        };

        if (establishments) {
          params.page.size = 999999;
        }

        if (this.organization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.organization,
            },
          };
        }
        if (this.filterServiceCenters) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              serviceCenter: this.filterServiceCenters,
            },
          };
        }

        await this.$store.dispatch("establishments/list", params);
        const establishmentsArr = await this.$store.getters["establishments/list"];
        this.establishmentsList = {};
        establishmentsArr.forEach((establishment) => {
          this.establishmentsList[establishment.id] = establishment;
        });
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    async establishmentsChanged(establishments) {
      this.$emit(
        "establishmentsChanged",
        [...establishments].map((item) => ({
          type: "establishments",
          id: item,
        }))
      );
    },

    isInactive(establishment) {
      return (
        !!establishment &&
        (establishment.active === false || establishment.active === 0)
      );
    },

    optionLabel(establishment) {
      return this.isInactive(establishment)
        ? `${establishment.company_name} (${this.$t("ESTABLISHMENTS.INACTIVE")})`
        : `${establishment.company_name}`;
    },
  },

  watch: {
    establishments(establishments) {
      if (establishments && establishments.length != 0) {
        const establishmentsData = establishments.map((item) => item.id);
        if (difference(establishmentsData, this.establishmentsModel).length !== 0) {
          this.establishmentsModel = establishmentsData;
          this.getEstablishments(null, establishments);
        }
      } else {
        this.establishmentsModel = [];
      }
    },
    organization(organization) {
      this.getEstablishments();
    },
    filterServiceCenters: {
      handler: function (establishments) {
        this.getEstablishments(null, establishments);
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
